import React, { useMemo, useRef, useState } from 'react';
import styled from 'styled-components';
import {
  AssistantM1Error,
  CanonicalTask,
  AssistantSourceReady,
  createAssistantSource,
  createTaskProposal,
  confirmTaskProposal,
  evidenceView,
  getTaskProposalResult,
  ProposalState,
  TaskProposal,
} from '../utils/assistantM1';
import { getGlobalToken } from '../utils/authStorage';

const Page = styled.div`
  max-width: 980px;
  margin: 0 auto;
  padding: 2.5rem 1.25rem 4rem;
`;
const Header = styled.div`margin-bottom: 1.5rem;`;
const Title = styled.h1`margin: 0; color: ${({ theme }) => theme.text};`;
const Subtitle = styled.p`color: ${({ theme }) => theme.textSecondary}; line-height: 1.7;`;
const Card = styled.section`
  border: 1px solid ${({ theme }) => theme.border};
  border-radius: 18px;
  background: ${({ theme }) => theme.surface};
  color: ${({ theme }) => theme.text};
  box-shadow: ${({ theme }) => theme.shadow};
  padding: 1.25rem;
`;
const Composer = styled.div`display: grid; gap: 0.75rem;`;
const Field = styled.input`
  border: 1px solid ${({ theme }) => theme.border};
  border-radius: 12px;
  background: ${({ theme }) => theme.surfaceAlt};
  color: ${({ theme }) => theme.text};
  padding: 0.75rem 0.85rem;
  font: inherit;
`;
const Textarea = styled.textarea`
  min-height: 110px;
  resize: vertical;
  border: 1px solid ${({ theme }) => theme.border};
  border-radius: 12px;
  background: ${({ theme }) => theme.surfaceAlt};
  color: ${({ theme }) => theme.text};
  padding: 0.85rem;
  font: inherit;
`;
const ButtonRow = styled.div`display: flex; gap: 0.75rem; flex-wrap: wrap;`;
const Button = styled.button<{ $primary?: boolean }>`
  border: 1px solid ${({ theme, $primary }) => ($primary ? theme.primary : theme.border)};
  border-radius: 10px;
  background: ${({ theme, $primary }) => ($primary ? theme.primary : theme.surfaceAlt)};
  color: ${({ theme, $primary }) => ($primary ? '#fff' : theme.text)};
  padding: 0.65rem 1rem;
  font-weight: 700;
  cursor: pointer;
  &:disabled { opacity: 0.55; cursor: not-allowed; }
`;
const Status = styled.span`
  display: inline-flex;
  padding: 0.35rem 0.6rem;
  border-radius: 999px;
  background: ${({ theme }) => theme.surfaceAlt};
  color: ${({ theme }) => theme.primary};
  font-size: 0.82rem;
  font-weight: 800;
`;
const ProposalHeader = styled.div`display: flex; justify-content: space-between; gap: 1rem; align-items: flex-start;`;
const ProposalTitle = styled.h2`margin: 0.35rem 0 0;`;
const EvidenceGrid = styled.div`display: grid; gap: 0.75rem; margin-top: 0.8rem;`;
const Evidence = styled.article`
  padding: 0.85rem;
  border-radius: 12px;
  border: 1px solid ${({ theme }) => theme.border};
  background: ${({ theme }) => theme.surfaceAlt};
`;
const Meta = styled.div`color: ${({ theme }) => theme.textSecondary}; font-size: 0.88rem; line-height: 1.6; word-break: break-word;`;
const Outcome = styled.div<{ $kind: 'success' | 'warning' | 'error' }>`
  margin-top: 1rem;
  padding: 0.9rem;
  border-radius: 12px;
  background: ${({ $kind }) => $kind === 'success' ? 'rgba(46, 204, 113, 0.13)' : $kind === 'error' ? 'rgba(231, 76, 60, 0.13)' : 'rgba(255, 181, 71, 0.16)'};
`;

export default function Assistant() {
  const [intent, setIntent] = useState('');
  const [state, setState] = useState<ProposalState>('IDLE');
  const [proposal, setProposal] = useState<TaskProposal | null>(null);
  const [task, setTask] = useState<CanonicalTask | null>(null);
  const [message, setMessage] = useState('');
  const [knowledgeOpen, setKnowledgeOpen] = useState(false);
  const [knowledgeTitle, setKnowledgeTitle] = useState('');
  const [knowledgeContent, setKnowledgeContent] = useState('');
  const [knowledgeLogicalName, setKnowledgeLogicalName] = useState('');
  const [knowledgeStatus, setKnowledgeStatus] = useState<'IDLE' | 'SUBMITTING' | 'READY' | 'FAILED'>('IDLE');
  const [knowledgeMessage, setKnowledgeMessage] = useState('');
  const [readySource, setReadySource] = useState<AssistantSourceReady | null>(null);
  const inFlight = useRef(false);
  const sourceInFlight = useRef(false);
  const loggedIn = !!getGlobalToken();
  const evidence = useMemo(() => proposal ? evidenceView(proposal) : [], [proposal]);

  const draft = async (intentOverride?: string) => {
    if (inFlight.current || state === 'UNKNOWN') return;
    const requestedIntent = (intentOverride ?? intent).trim();
    if (!requestedIntent) return;
    inFlight.current = true;
    setState('LOADING');
    setMessage('');
    setProposal(null);
    setTask(null);
    try {
      setIntent(requestedIntent);
      const result = await createTaskProposal(requestedIntent);
      if (!result.proposal) {
        setState(result.status);
        setMessage(result.message);
        return;
      }
      setProposal(result.proposal);
      setState('READY');
    } catch (error) {
      const failure = error instanceof AssistantM1Error ? error : new AssistantM1Error('Friday could not prepare a Proposal.');
      setState(failure.outcomeUnknown ? 'UNKNOWN' : 'FAILED');
      setMessage(failure.outcomeUnknown
        ? 'The Proposal request was interrupted. Friday will not retry automatically while the canonical result is unknown.'
        : failure.message);
    } finally {
      inFlight.current = false;
    }
  };

  const addKnowledge = async () => {
    if (sourceInFlight.current || knowledgeStatus === 'SUBMITTING') return;
    sourceInFlight.current = true;
    setKnowledgeStatus('SUBMITTING');
    setKnowledgeMessage('');
    setReadySource(null);
    try {
      const result = await createAssistantSource({
        title: knowledgeTitle,
        content: knowledgeContent,
        ...(knowledgeLogicalName.trim() ? { logicalName: knowledgeLogicalName } : {}),
      });
      setReadySource(result);
      setKnowledgeStatus('READY');
      setKnowledgeMessage('Knowledge added');
    } catch (error) {
      const failure = error instanceof AssistantM1Error ? error : new AssistantM1Error('Friday could not add this knowledge.');
      const messages: Record<string, string> = {
        INVALID_SOURCE: 'Check the title and content, then try again.',
        SOURCE_FORBIDDEN: 'Your account is not allowed to add this knowledge.',
        SOURCE_INGESTION_FAILED: 'Friday could not make this knowledge ready. Your draft has been preserved.',
        UPSTREAM_ERROR: 'The knowledge service is temporarily unavailable. Your draft has been preserved.',
      };
      setKnowledgeStatus('FAILED');
      setKnowledgeMessage(messages[failure.code] ?? failure.message);
    } finally {
      sourceInFlight.current = false;
    }
  };

  const proposeFromReadySource = () => {
    if (!readySource || knowledgeStatus !== 'READY') return;
    void draft(readySource.title);
  };

  const resetKnowledgeForm = () => {
    if (knowledgeStatus === 'SUBMITTING') return;
    setKnowledgeTitle('');
    setKnowledgeContent('');
    setKnowledgeLogicalName('');
    setKnowledgeStatus('IDLE');
    setKnowledgeMessage('');
    setReadySource(null);
  };

  const confirm = async () => {
    if (!proposal || state !== 'READY' || inFlight.current) return;
    inFlight.current = true;
    setState('CONFIRMING');
    setMessage('');
    try {
      const result = await confirmTaskProposal(proposal.proposalId);
      setProposal(result.proposal);
      setTask(result.task);
      setState('CREATED');
    } catch (error) {
      const failure = error instanceof AssistantM1Error ? error : new AssistantM1Error('Friday could not confirm this Proposal.');
      setState(failure.outcomeUnknown ? 'UNKNOWN' : 'FAILED');
      setMessage(failure.outcomeUnknown
        ? 'Confirmation was interrupted. The Task may or may not exist. Friday will not submit another confirmation automatically.'
        : failure.message);
    } finally {
      inFlight.current = false;
    }
  };

  const cancel = () => {
    if (state !== 'READY' || inFlight.current) return;
    setTask(null);
    setState('CANCELLED');
    setMessage('Proposal dismissed. No canonical Task was created.');
  };

  const recheck = async () => {
    if (!proposal || state !== 'UNKNOWN' || inFlight.current) return;
    inFlight.current = true;
    setState('RECHECKING');
    setMessage('Checking the authoritative Task result…');
    try {
      const result = await getTaskProposalResult(proposal.proposalId);
      if (result.status === 'CREATED') {
        setTask(result.task);
        setState('CREATED');
        setMessage('The canonical Task was created. No confirmation was resubmitted.');
      } else {
        setTask(null);
        setState('READY');
        setMessage('The Proposal is still pending. No Task exists; you may confirm or cancel.');
      }
    } catch (error) {
      const failure = error instanceof AssistantM1Error ? error : new AssistantM1Error('Friday could not check the canonical result.');
      setState('UNKNOWN');
      setMessage(`The outcome is still unknown. ${failure.message}`);
    } finally {
      inFlight.current = false;
    }
  };

  if (!loggedIn) {
    return (
      <Page>
        <Header><Title>Friday Assistant</Title><Subtitle>This is a private account feature. Sign in before requesting Atlas-backed task proposals.</Subtitle></Header>
        <Card>
          <Button $primary onClick={() => window.dispatchEvent(new CustomEvent('open-auth-modal'))}>Login / Register</Button>
        </Card>
      </Page>
    );
  }

  return (
    <Page>
      <Header>
        <Title>Friday Assistant</Title>
        <Subtitle>Turn authorized Atlas evidence into one reviewable Task Proposal. Nothing is created until you confirm.</Subtitle>
      </Header>
      <Card>
        <Composer>
          <label htmlFor="assistant-intent"><strong>What should Friday help you follow up?</strong></label>
          <Textarea id="assistant-intent" value={intent} onChange={(event) => setIntent(event.target.value)} placeholder="Describe one concrete follow-up based on your knowledge…" disabled={state === 'LOADING' || state === 'CONFIRMING' || state === 'UNKNOWN'} />
          <ButtonRow>
            <Button $primary onClick={() => void draft()} disabled={!intent.trim() || state === 'LOADING' || state === 'CONFIRMING' || state === 'UNKNOWN'}>
              {state === 'LOADING' ? 'Preparing…' : 'Create knowledge-backed Proposal'}
            </Button>
            <Button onClick={() => setKnowledgeOpen((open) => !open)} disabled={knowledgeStatus === 'SUBMITTING'}>
              {knowledgeOpen ? 'Close Add Knowledge' : 'Add Knowledge'}
            </Button>
          </ButtonRow>
        </Composer>
      </Card>

      {knowledgeOpen ? (
        <Card style={{ marginTop: '1rem' }} aria-live="polite">
          <ProposalHeader>
            <div><Meta>Knowledge onboarding</Meta><ProposalTitle>Add Knowledge</ProposalTitle></div>
            <Status>{knowledgeStatus}</Status>
          </ProposalHeader>
          <Composer style={{ marginTop: '1rem' }}>
            <label htmlFor="knowledge-title"><strong>Title</strong></label>
            <Field id="knowledge-title" value={knowledgeTitle} onChange={(event) => setKnowledgeTitle(event.target.value)} disabled={knowledgeStatus === 'SUBMITTING' || knowledgeStatus === 'READY'} maxLength={200} />
            <label htmlFor="knowledge-content"><strong>Content</strong></label>
            <Textarea id="knowledge-content" value={knowledgeContent} onChange={(event) => setKnowledgeContent(event.target.value)} disabled={knowledgeStatus === 'SUBMITTING' || knowledgeStatus === 'READY'} maxLength={200000} />
            <label htmlFor="knowledge-logical-name"><strong>Logical Name</strong> <Meta>(optional, use a unique keyword)</Meta></label>
            <Field id="knowledge-logical-name" value={knowledgeLogicalName} onChange={(event) => setKnowledgeLogicalName(event.target.value)} disabled={knowledgeStatus === 'SUBMITTING' || knowledgeStatus === 'READY'} maxLength={200} />
            <ButtonRow>
              <Button $primary onClick={() => void addKnowledge()} disabled={!knowledgeTitle.trim() || !knowledgeContent.trim() || knowledgeStatus === 'SUBMITTING'}>
                {knowledgeStatus === 'SUBMITTING' ? 'Adding…' : 'Add Knowledge'}
              </Button>
              {knowledgeStatus === 'READY' && readySource ? (
                <>
                  <Button onClick={proposeFromReadySource} disabled={state === 'LOADING' || inFlight.current}>
                    Create knowledge-backed Proposal
                  </Button>
                  <Button onClick={resetKnowledgeForm} disabled={state === 'LOADING' || inFlight.current}>Add another</Button>
                </>
              ) : null}
            </ButtonRow>
          </Composer>
          {knowledgeStatus === 'READY' && readySource ? (
            <Outcome $kind="success"><strong>{knowledgeMessage}</strong><div>{readySource.title}</div><Meta>READY</Meta></Outcome>
          ) : null}
          {knowledgeStatus === 'FAILED' ? (
            <Outcome $kind="error"><strong>Knowledge not added</strong><div>{knowledgeMessage}</div></Outcome>
          ) : null}
        </Card>
      ) : null}

      {proposal ? (
        <Card style={{ marginTop: '1rem' }} aria-live="polite">
          <ProposalHeader>
            <div><Meta>Proposal</Meta><ProposalTitle>{proposal.title}</ProposalTitle></div>
            <Status>{state}</Status>
          </ProposalHeader>
          <p>{proposal.description}</p>
          {proposal.interpretation?.evidenceStatement ? <Meta>{proposal.interpretation.evidenceStatement}</Meta> : null}
          <h3>Why Friday suggested this</h3>
          <EvidenceGrid>
            {evidence.map((item) => (
              <Evidence key={item.id}>
                <strong>{item.title}</strong>
                <Meta>{item.label}</Meta>
                <Meta>{item.location}</Meta>
                <Meta>Freshness: {item.freshness}{item.sourceTime ? ` · Source time: ${item.sourceTime}` : ''}</Meta>
              </Evidence>
            ))}
          </EvidenceGrid>
          {(proposal.warnings ?? []).map((warning) => <Outcome key={warning.code} $kind="warning">{warning.message}</Outcome>)}

          {state === 'READY' || state === 'CONFIRMING' ? (
            <ButtonRow style={{ marginTop: '1rem' }}>
              <Button onClick={cancel} disabled={state !== 'READY' || inFlight.current}>Cancel</Button>
              <Button $primary onClick={() => void confirm()} disabled={state !== 'READY' || inFlight.current || evidence.length === 0}>
                {state === 'CONFIRMING' ? 'Confirming…' : 'Confirm and create Task'}
              </Button>
            </ButtonRow>
          ) : null}
          {state === 'UNKNOWN' || state === 'RECHECKING' ? (
            <ButtonRow style={{ marginTop: '1rem' }}>
              <Button $primary onClick={() => void recheck()} disabled={state === 'RECHECKING' || inFlight.current}>
                {state === 'RECHECKING' ? 'Checking…' : 'Check Result'}
              </Button>
            </ButtonRow>
          ) : null}
        </Card>
      ) : null}

      {state === 'CREATED' && task ? <Outcome $kind="success"><strong>Created</strong><div>{task.title}</div><Meta>One canonical Task has been created.</Meta>{message ? <Meta>{message}</Meta> : null}</Outcome> : null}
      {state === 'READY' && message ? <Outcome $kind="warning"><strong>Proposal Ready</strong><div>{message}</div></Outcome> : null}
      {state === 'CANCELLED' ? <Outcome $kind="warning"><strong>Cancelled</strong><div>{message}</div></Outcome> : null}
      {state === 'NO_KNOWLEDGE' ? (
        <Outcome $kind="warning" aria-live="polite">
          <strong>No knowledge yet</strong>
          <div>{message || 'There is no authorized knowledge available for this request.'}</div>
          <Meta>Add knowledge here, then explicitly create a Proposal. No Proposal or Task was created by this empty result.</Meta>
          <ButtonRow style={{ marginTop: '0.75rem' }}><Button onClick={() => setKnowledgeOpen(true)}>Add Knowledge</Button></ButtonRow>
        </Outcome>
      ) : null}
      {state === 'NO_MATCHING_EVIDENCE' ? (
        <Outcome $kind="warning" aria-live="polite">
          <strong>No matching evidence</strong>
          <div>{message || 'Your knowledge is available, but it did not contain evidence for this request.'}</div>
          <Meta>Revise the request or add relevant knowledge. No Proposal or Task was created.</Meta>
          <ButtonRow style={{ marginTop: '0.75rem' }}><Button onClick={() => setKnowledgeOpen(true)}>Add Knowledge</Button></ButtonRow>
        </Outcome>
      ) : null}
      {state === 'UNKNOWN_EMPTY' ? (
        <Outcome $kind="warning" aria-live="polite">
          <strong>No usable context</strong>
          <div>{message || 'Friday received an empty legacy Context response.'}</div>
          <Meta>No Proposal or Task was created.</Meta>
        </Outcome>
      ) : null}
      {state === 'FAILED' ? <Outcome $kind="error"><strong>Failed</strong><div>{message}</div></Outcome> : null}
      {state === 'UNKNOWN' || state === 'RECHECKING' ? <Outcome $kind="warning"><strong>Unknown</strong><div>{message}</div><Meta>Checking is read-only and never resubmits confirmation.</Meta></Outcome> : null}
    </Page>
  );
}
